import { useContext, useEffect, useState, useRef, useMemo, useCallback } from "react";
import { Grid, Autocomplete, TextField, Tooltip, InputAdornment } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import styles from "./styles.module.css";
import WhiteTextField from "components/utils/CustomFields/TextFieldBGWhite";
import SalesPersonContext from "context/SalesPersonContext";
import CustomerContext from "context/CustomerContext";
import ShopContext from "context/ShopContext";
import ProductContext from "context/ProductContext";
import SalesContext from "context/SalesContext";
import {
  KEYPAD_BUTTONS,
  SALE_STATUS,
  INVOICE_FORMATS,
  KEYBOARD_KEYS,
  PAYMENT_MODES,
  INVOICE_ACTIONS,
  SCANNER_RESTRICTED_INPUTS,
  HTML_TAGS,
  ORDER_TYPES,
  SALE_SYNC_STATUS,
  UPI_MODES,
  DEFAULT_PAYMENT_MODE_ORDER,
  PAYMENT_MODE_ORDER_KEYS,
  TAX_PREFERENCES,
  EVENTS,
  KEYPAD_SCANNER_DEBOUNCE_MS,
  SALE_DIALOG_STATUS,
} from "const";
import { customFilterOptions } from "helpers/SearchHelper";
import { isScanningBarcode, restoreInputElementValue } from "helpers/BarcodeHelper";
import BarcodeScanner from "components/utils/BarcodeScanner/BarcodeScanner";
import { eventsEmitter } from "utils/events";
import useWebInvoicePrint from "helpers/InvoiceHelper";
import TaxHelper from "helpers/TaxHelper";
import CartHelper from "helpers/CartHelper";
import * as TerminalHelper from "helpers/TermialHelper";
import * as SalesHelper from "helpers/SalesHelper";
import * as ShiftHelper from "helpers/ShiftHelper";
import * as NumberFormatHelper from "helpers/NumberFormatHelper";
import * as InvoiceActionHelper from "helpers/InvoiceActionHelper";
import A4Invoice from "components/utils/PrintInvoice/A4Invoice";
import A5Invoice from "components/utils/PrintInvoice/A5Invoice";
import ThreeInchInvoice from "components/utils/PrintInvoice/ThreeInchInvoice";
import BalanceDialog from "components/popups/SaleEditDialog/BalanceDialog";
import KeyPadVertical from "components/utils/CustomKeypad/KeyPadVertical";
import CartDeleteConfirmPopup from "components/popups/CartDeleteConfirmPopup/CartDeleteConfirmPopup";
import AddCustomer from "components/popups/AddCustomer";
import CloseIcon from "@mui/icons-material/Close";
import { ReactComponent as CalendarIcon } from "images/svg/calendar.svg";
import CheckoutActionMenu from "components/utils/CheckoutActionMenu/CheckoutActionMenu";
import ERPTable from "./ERPTable";
import toast from "react-hot-toast";
import CustomerHelper from "helpers/CustomerHelper";
import KotManager from "helpers/KotManager";
import * as EstimateHelper from "helpers/EstimateHelper";
import GeneratePaymentQrDialog from "components/popups/GeneratePaymentQrDialog/GeneratePaymentQrDialog";
import SaleStatusDialog from "components/popups/SaleStatusDialog/SaleStatusDialog";

function ERPDashboard() {
  const { salesPersons } = useContext(SalesPersonContext);
  const { customers, addCustomer, updateCustomerAfterSale } = useContext(CustomerContext);
  const { selectedShop, paymentModes: contextPaymentModes } = useContext(ShopContext) || {};
  const { availableItems, priceCategories, priceCategoryItems, categories } = useContext(ProductContext);

  const {
    sales,
    activeSale,
    setActiveSale,
    handleAddItemToSale,
    handleSetCustomer,
    handleSetSalesPerson,
    handleSetDate,
    handleDeleteSale,
    saleItemUpdateHandler,
    increaseSaleItemInActiveSale,
    reduceSaleItemFromActiveSale,
    removeSaleItemFromActiveSale,
    createNewSale,
    refreshSales,
    selectedPriceCategory,
    handleChangePriceCategory,
  } = useContext(SalesContext);

  const [selectedSalesPerson, setSelectedSalesPerson] = useState(null);
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [customerSearchText, setCustomerSearchText] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [saleItems, setCartItems] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productSearchText, setProductSearchText] = useState("");
  const [itemQuantity, setItemQuantity] = useState(1);
  const [itemDiscountPercentage, setItemDiscountPercentage] = useState(0);
  const [itemTaxPercentage, setItemTaxPercentage] = useState(0);
  const [itemRate, setItemRate] = useState(0);
  const [paymentModes, setPaymentModes] = useState([]);
  const [activeMode, setActiveMode] = useState(null);
  const [keypadString, setKeypadString] = useState("");
  const [printSale, setPrintSale] = useState(activeSale);
  const [balancePayments, setBalancePayments] = useState([]);
  const [showBalanceDialog, setShowBalanceDialog] = useState(false);
  const [showPaymentQrDialog, setShowPaymentQrDialog] = useState(false);
  const [saleDialogState, setSaleDialogState] = useState({
    open: false,
    status: SALE_DIALOG_STATUS.PROCESSING,
    action: null,
  });
  const [saleToDelete, setSaleToDelete] = useState(null);
  const loadedInvoiceIdRef = useRef(null);
  const enterPrintConfirmCountRef = useRef(0);

  const activeInvoices = useMemo(() => {
    const list = sales.filter((inv) => (!inv.status || inv.status === SALE_STATUS.DRAFT) && !inv.bill_no);
    const isNewOrDraft =
      !activeSale?.local_id || ((!activeSale.status || activeSale.status === SALE_STATUS.DRAFT) && !activeSale.bill_no);

    if (activeSale?.local_id && isNewOrDraft && !list.some((inv) => inv.local_id === activeSale.local_id)) {
      list.push(activeSale);
    }

    list.sort((a, b) => {
      const ta = a?.time ? new Date(a.time).getTime() : (a.id ?? 0);
      const tb = b?.time ? new Date(b.time).getTime() : (b.id ?? 0);
      return tb - ta;
    });

    return list;
  }, [sales, activeSale]);

  const selectedInvoice = useMemo(() => {
    if (!activeSale?.local_id) return 0;
    const index = activeInvoices.findIndex((inv) => inv.local_id === activeSale.local_id);
    return index !== -1 ? index : 0;
  }, [activeSale, activeInvoices]);

  const [isInvoiceInitialized, setIsInvoiceInitialized] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function initializeActiveInvoice() {
      if (isInvoiceInitialized) return;
      if (!activeSale?.local_id) {
        const activeSales = await refreshSales();
        if (!isMounted) return;
        const drafts = (activeSales || []).filter(
          (inv) => (!inv.status || inv.status === SALE_STATUS.DRAFT) && !inv.bill_no,
        );
        if (drafts.length > 0) {
          setActiveSale(drafts[0]);
        } else {
          await createNewSale();
        }
      }
      if (isMounted) {
        setIsInvoiceInitialized(true);
      }
    }

    initializeActiveInvoice();

    return () => {
      isMounted = false;
    };
  }, [isInvoiceInitialized, activeSale?.local_id, refreshSales, setActiveSale, createNewSale]);

  const amountReceived = paymentModes.filter((m) => m.name !== "Discount").reduce((sum, m) => sum + m.amount, 0);

  const discount = paymentModes.find((m) => m.name === "Discount")?.amount || 0;

  const grossPayable = (activeSale?.payable || 0) + (activeSale?.extraDiscount || 0);
  const prepaidAmount = activeSale?.prepaid || activeSale?.previous_payable || 0;
  const due = Math.max(grossPayable - (amountReceived + discount + prepaidAmount), 0);
  const change = Math.max(amountReceived + discount + prepaidAmount - grossPayable, 0);

  const currentShiftId = ShiftHelper.getCurrentShiftId();

  const resetProductStagingState = useCallback(() => {
    setSelectedProduct(null);
    setProductSearchText("");
    setItemQuantity(1);
    setItemDiscountPercentage(0);
    setItemTaxPercentage(0);
    setItemRate(0);
  }, []);

  const resetLocalState = useCallback(() => {
    setPaymentModes((prev) =>
      prev.map((mode) => ({
        ...mode,
        amount: 0,
      })),
    );

    setKeypadString("");
    setActiveMode(null);
    resetProductStagingState();
  }, [resetProductStagingState]);

  // 1. Sync cart items when activeSale items change
  useEffect(() => {
    if (activeSale) {
      const dbItems = activeSale.items || [];
      setCartItems((prev) => {
        if (JSON.stringify(prev) !== JSON.stringify(dbItems)) {
          return dbItems;
        }
        return prev;
      });
    } else {
      setCartItems((prev) => (prev.length > 0 ? [] : prev));
    }
  }, [activeSale?.items, activeSale]);

  // 2. Sync selected customer and salesperson only when the invoice ID changes
  useEffect(() => {
    if (activeSale) {
      const isInvoiceChanged = loadedInvoiceIdRef.current !== activeSale.local_id;

      if (isInvoiceChanged) {
        loadedInvoiceIdRef.current = activeSale.local_id;

        const customerList = customers || [];

        let dbCustomer = null;

        if (activeSale.customer && (activeSale.customer.id || activeSale.customer.local_id)) {
          dbCustomer = customerList.find((c) => {
            if (activeSale.customer.id && c.id) {
              return String(c.id) === String(activeSale.customer.id);
            }
            if (activeSale.customer.local_id && c.local_id) {
              return String(c.local_id) === String(activeSale.customer.local_id);
            }
            return false;
          });
        }

        setSelectedCustomer(dbCustomer || (activeSale.customer?.id ? activeSale.customer : null));

        const dbSalesperson = Array.isArray(salesPersons)
          ? salesPersons.find((p) => p.id === activeSale.salesperson?.id)
          : null;
        setSelectedSalesPerson(dbSalesperson || null);

        setKeypadString("");
        enterPrintConfirmCountRef.current = 0;
        setPaymentModes((prev) =>
          prev.map((mode) => ({
            ...mode,
            amount: 0,
          })),
        );
      }
    } else {
      loadedInvoiceIdRef.current = null;
      setSelectedCustomer(null);
      setSelectedSalesPerson(null);
      setPaymentModes((prev) =>
        prev
          .filter((m) => m.name !== PAYMENT_MODES.CREDIT && m.name !== PAYMENT_MODES.POINTS)
          .map((mode) => ({ ...mode, amount: 0 })),
      );
    }
  }, [activeSale?.local_id, customers, salesPersons, activeSale]);

  const handleSalesPersonChange = (event, newValue) => {
    setSelectedSalesPerson(newValue);
    handleSetSalesPerson(newValue);
  };

  const productInputRef = useRef(null);
  const customerInputRef = useRef(null);
  const keyPadRef = useRef(null);
  const paymentTabsRef = useRef(null);
  const printButtonRef = useRef(null);

  const focusProductInput = useCallback(() => {
    setTimeout(() => {
      if (productInputRef.current) {
        productInputRef.current.focus();
        productInputRef.current.select?.();
      }
    }, 100);
  }, []);

  const openSaleProcessing = useCallback((action) => {
    setSaleDialogState({ open: true, status: SALE_DIALOG_STATUS.PROCESSING, action });
  }, []);

  const openSaleSuccess = useCallback((action) => {
    setSaleDialogState({ open: true, status: SALE_DIALOG_STATUS.SUCCESS, action });
  }, []);

  const closeSaleDialog = useCallback(() => {
    setSaleDialogState({ open: false, status: SALE_DIALOG_STATUS.PROCESSING, action: null });
  }, []);

  const handleCustomerChange = async (event, newValue) => {
    if (newValue) {
      setSelectedCustomer(newValue);
      if (!activeSale?.local_id) {
        try {
          setLoading(true);
          const fullCustomer = {
            ...newValue,
            mobile: newValue.mobile || "",
          };
          await createNewSale({ customer: fullCustomer });
        } catch (err) {
          console.error("Failed to create new sale with customer", err);
        } finally {
          setLoading(false);
        }
      } else {
        handleSetCustomer(newValue);
      }
    } else {
      setSelectedCustomer(null);
      handleSetCustomer(null);
    }
  };

  useEffect(() => {
    if (activeMode && paymentTabsRef.current) {
      const activeEl = paymentTabsRef.current.querySelector(`.${styles.activeTab}`);
      if (activeEl) {
        activeEl.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "nearest",
        });
      }
    }
  }, [activeMode]);

  const handleAddProduct = useCallback(
    async ({ product, rate = null, quantity = 1, discountPercentage = 0, taxPercentage }) => {
      await handleAddItemToSale(product, Number(quantity || 1), "", rate, null, {
        discountPercentage: Number(discountPercentage || 0),
        taxPercentage: taxPercentage,
        manualRate: rate,
      });
      resetProductStagingState();

      // Focus back to product search
      setTimeout(() => {
        productInputRef.current?.focus();
      }, 0);
    },
    [handleAddItemToSale, resetProductStagingState],
  );

  const getEffectivePrice = useCallback(
    (product) => {
      if (!product) return 0;
      let effectivePrice = null;
      if (selectedCustomer && selectedCustomer.group) {
        effectivePrice = CartHelper.getApplicableCustomerPrice(product.id, 1, selectedCustomer, priceCategoryItems);
      } else if (selectedPriceCategory) {
        effectivePrice = CartHelper.getApplicableCategoryPrice(
          product.id,
          1,
          selectedPriceCategory,
          priceCategoryItems,
        );
      }

      return effectivePrice ?? product.price;
    },
    [selectedPriceCategory, selectedCustomer, priceCategoryItems],
  );

  const handleAddSelectedProduct = useCallback(async () => {
    if (!selectedProduct) return;

    const defaultPrice = getEffectivePrice(selectedProduct);
    const defaultUnitRate = TaxHelper.getItemUnitRate(
      defaultPrice,
      itemTaxPercentage,
      selectedProduct.cessPercentage ?? selectedProduct.cess ?? 0,
      itemDiscountPercentage,
    );
    const isRateModified = Number(itemRate) !== NumberFormatHelper.amountExtendedRoundOff(defaultUnitRate);

    handleAddProduct({
      product: selectedProduct,
      rate: isRateModified ? Number(itemRate) : null,
      quantity: Number(itemQuantity || 1),
      discountPercentage: itemDiscountPercentage,
      taxPercentage: itemTaxPercentage,
    });
  }, [
    itemQuantity,
    itemDiscountPercentage,
    itemTaxPercentage,
    itemRate,
    selectedProduct,
    getEffectivePrice,
    handleAddProduct,
  ]);

  function computeERPKeypadAmount(val, keypadString, activeMode, selectedCustomer, selectedShop, paymentModes) {
    val = String(val);
    let amountStr = keypadString;

    if (val === KEYPAD_BUTTONS.CLEAR) {
      amountStr = "";
    } else if (val === KEYPAD_BUTTONS.DELETE) {
      amountStr = amountStr.slice(0, -1) || "";
      if (amountStr === "-") amountStr = "";
    } else if (val === KEYPAD_BUTTONS.DOT) {
      if (!amountStr.includes(".")) {
        amountStr = amountStr === "" ? "0." : amountStr + ".";
      }
    } else if (val === KEYPAD_BUTTONS.PLUS_MINUS) {
      if (amountStr.startsWith("-")) amountStr = amountStr.slice(1);
      else amountStr = "-" + amountStr;
    } else if (val.startsWith("+")) {
      const increment = Number(val) || 0;
      const newNum = (parseFloat(amountStr) || 0) + increment;
      amountStr = newNum.toString();
    } else if (!isNaN(val) && val.length === 1) {
      amountStr += val;
    }

    let finalAmount = parseFloat(amountStr) || 0;

    if (activeMode === PAYMENT_MODES.CREDIT && selectedCustomer?.id) {
      const activeModeObj = paymentModes.find((m) => m.name === activeMode);
      const maxAllowed = CustomerHelper.getAvailableCredit(selectedCustomer) + (activeModeObj?.initialAmount || 0);

      if (finalAmount > maxAllowed) {
        toast.error(`Amount exceeds available credit limit (${maxAllowed})`);
        finalAmount = maxAllowed;
        amountStr = maxAllowed.toString();
      }
    }

    if (activeMode === PAYMENT_MODES.POINTS && selectedCustomer?.id) {
      const activeModeObj = paymentModes.find((m) => m.name === activeMode);
      const points = Number(selectedCustomer.loyalty_points || selectedCustomer.points || 0);
      const conversionRate = Number(selectedShop?.loyalty_point_conversion_rate || 1);
      const maxAllowedCurrency = points * conversionRate + (activeModeObj?.initialAmount || 0);

      if (finalAmount > maxAllowedCurrency) {
        toast.error(`Amount exceeds available points value (${maxAllowedCurrency})`);
        finalAmount = maxAllowedCurrency;
        amountStr = maxAllowedCurrency.toString();
      }
    }

    return { finalAmount, amountStr };
  }

  const lastKeypadInputTimeRef = useRef(0);

  const handleKeypadInput = (val) => {
    enterPrintConfirmCountRef.current = 0;
    lastKeypadInputTimeRef.current = Date.now();

    const { finalAmount, amountStr } = computeERPKeypadAmount(
      val,
      keypadString,
      activeMode,
      selectedCustomer,
      selectedShop,
      paymentModes,
    );

    setKeypadString(amountStr);

    setPaymentModes((prevModes) =>
      prevModes.map((mode) => {
        if (mode.name !== activeMode) return mode;

        return {
          ...mode,
          amount: finalAmount,
        };
      }),
    );
  };

  const sortedPaymentModes = useMemo(() => {
    const orderObj = selectedShop?.config?.payment_mode_order;
    const getOrder = (mode) => {
      const lower = mode.name.toLowerCase();
      if (orderObj && orderObj[lower] !== undefined) {
        return Number(orderObj[lower]);
      }
      if (mode.order !== undefined && mode.order !== null) {
        return Number(mode.order);
      }
      // Fallback default sorting:
      if (lower === "cash") return 0;
      if (lower === "credit") return DEFAULT_PAYMENT_MODE_ORDER.CREDIT;
      if (lower === "points" || lower === "loyalty points") return DEFAULT_PAYMENT_MODE_ORDER.POINTS;
      if (lower === "card") return 3;
      if (lower === "upi") return 4;
      if (lower === "discount") return DEFAULT_PAYMENT_MODE_ORDER.DISCOUNT;
      return 5;
    };
    return [...paymentModes].sort((a, b) => getOrder(a) - getOrder(b));
  }, [paymentModes, selectedShop]);

  const handleKeypadFocus = () => {
    if (sortedPaymentModes.length > 0) {
      setActiveMode(sortedPaymentModes[0].name);
      setKeypadString(sortedPaymentModes[0].amount !== 0 ? sortedPaymentModes[0].amount.toString() : "");
    }
  };

  const handleNextPaymentMode = () => {
    enterPrintConfirmCountRef.current = 0;
    if (!sortedPaymentModes.length) return;
    const currentIndex = sortedPaymentModes.findIndex((m) => m.name === activeMode);
    const nextIndex = (currentIndex + 1) % sortedPaymentModes.length;
    const nextMode = sortedPaymentModes[nextIndex];
    setActiveMode(nextMode.name);
    setKeypadString(nextMode.amount !== 0 ? nextMode.amount.toString() : "");
  };

  const handlePreviousPaymentMode = () => {
    enterPrintConfirmCountRef.current = 0;
    if (!sortedPaymentModes.length) return;
    const currentIndex = sortedPaymentModes.findIndex((m) => m.name === activeMode);
    const prevIndex = (currentIndex - 1 + sortedPaymentModes.length) % sortedPaymentModes.length;
    const prevMode = sortedPaymentModes[prevIndex];
    setActiveMode(prevMode.name);
    setKeypadString(prevMode.amount !== 0 ? prevMode.amount.toString() : "");
  };

  useEffect(() => {
    async function populatePaymentModes() {
      const paymentModes = await TerminalHelper.fetchPaymentModes({ setLoading });

      const modesWithAmounts = paymentModes.map((mode) => {
        return {
          ...mode,
          amount: 0,
        };
      });

      const orderObj = selectedShop?.config?.payment_mode_order;
      const discountOrder =
        orderObj && orderObj[PAYMENT_MODE_ORDER_KEYS.DISCOUNT] !== undefined
          ? orderObj[PAYMENT_MODE_ORDER_KEYS.DISCOUNT]
          : DEFAULT_PAYMENT_MODE_ORDER.DISCOUNT;

      modesWithAmounts.push({ id: null, name: "Discount", amount: 0, order: discountOrder });
      setPaymentModes(modesWithAmounts);
    }
    populatePaymentModes();
  }, [selectedShop?.config?.payment_mode_order]);

  function computeCustomerPaymentModes(prevModes, selectedCustomer, activeSale, selectedShop) {
    if (selectedCustomer?.id) {
      let changed = false;
      let next = [...prevModes];
      const orderObj = selectedShop?.config?.payment_mode_order;

      const hasCredit = next.some((m) => m.name === PAYMENT_MODES.CREDIT);
      if (!hasCredit) {
        const existing = (activeSale?.payment_modes || []).find(
          (paymentMode) => paymentMode.name === PAYMENT_MODES.CREDIT,
        );
        const creditAmount = existing ? Number(existing.amount) : Number(activeSale?.customer_credit_used || 0);
        next.push({
          id: PAYMENT_MODES.CREDIT,
          name: PAYMENT_MODES.CREDIT,
          amount: creditAmount,
          initialAmount: creditAmount,
          order:
            orderObj && orderObj[PAYMENT_MODE_ORDER_KEYS.CREDIT] !== undefined
              ? orderObj[PAYMENT_MODE_ORDER_KEYS.CREDIT]
              : DEFAULT_PAYMENT_MODE_ORDER.CREDIT,
        });
        changed = true;
      }
      const hasPoints = next.some((m) => m.name === PAYMENT_MODES.POINTS);
      if (!hasPoints) {
        const existing = (activeSale?.payment_modes || []).find(
          (paymentMode) => paymentMode.name === PAYMENT_MODES.POINTS,
        );
        const pointsAmount = existing ? Number(existing.amount) : Number(activeSale?.loyalty_points_amount || 0);
        next.push({
          id: PAYMENT_MODES.POINTS,
          name: PAYMENT_MODES.POINTS,
          amount: pointsAmount,
          initialAmount: pointsAmount,
          order:
            orderObj && orderObj[PAYMENT_MODE_ORDER_KEYS.POINTS] !== undefined
              ? orderObj[PAYMENT_MODE_ORDER_KEYS.POINTS]
              : DEFAULT_PAYMENT_MODE_ORDER.POINTS,
        });
        changed = true;
      }
      return changed ? next : prevModes;
    }
    const filtered = prevModes.filter((m) => m.name !== PAYMENT_MODES.CREDIT && m.name !== PAYMENT_MODES.POINTS);
    return filtered.length !== prevModes.length ? filtered : prevModes;
  }

  useEffect(() => {
    if (selectedCustomer?.id) {
      setPaymentModes((prev) => computeCustomerPaymentModes(prev, selectedCustomer, activeSale, selectedShop));
    } else {
      setPaymentModes((prev) => computeCustomerPaymentModes(prev, selectedCustomer, activeSale, selectedShop));
      setActiveMode((prev) => {
        if (prev === PAYMENT_MODES.CREDIT || prev === PAYMENT_MODES.POINTS) {
          setKeypadString("");
          return null;
        }
        return prev;
      });
    }
  }, [selectedCustomer, activeSale, selectedShop]);

  const printComponentRef = useRef(null);

  const handleWebInvoicePrint = useWebInvoicePrint({
    contentRef: printComponentRef,
    documentTitle: `Invoice-${printSale?.bill_no || activeSale?.bill_no || "NA"}`,
    setPrintSale,
  });

  async function finalizeEstimateOrSaleSync(isEstimate, finalSale, selectedCustomer, updateCustomerAfterSale) {
    try {
      if (isEstimate) {
        const res = await EstimateHelper.syncToAPI(finalSale, selectedCustomer);
        const syncedEstimate = {
          ...finalSale,
          sync_status: SALE_SYNC_STATUS.SYNCED_TO_CLOUD,
          id: res?.data?.id || finalSale.id,
        };
        await SalesHelper.saveSaleInDB(syncedEstimate, { notify: true });
      } else {
        const syncStatus = await SalesHelper.syncSaleAndPending(finalSale);
        const finalizedSale = { ...finalSale, sync_status: syncStatus };
        await SalesHelper.saveSaleInDB(finalizedSale);
        if (updateCustomerAfterSale) {
          await updateCustomerAfterSale(finalizedSale, finalizedSale.sync_status);
        }
      }
    } catch (error) {
      console.error("Background sync failed:", error);
    }
  }

  async function prepareEstimateForPrint(activeSale) {
    const isNewEstimate = !activeSale?.estimate_no;
    const finalSale = await EstimateHelper.assignEstimateNumber(activeSale);
    finalSale.status = SALE_STATUS.DRAFT;
    finalSale.sync_status = SALE_SYNC_STATUS.PENDING;
    await SalesHelper.saveSaleInDB(finalSale);
    if (isNewEstimate) {
      EstimateHelper.incrementEstimateSuffix();
    }
    return finalSale;
  }

  // @skip-max-lines
  async function prepareRegularSaleForPrint({
    activeSale,
    isNonChargeable,
    paymentModes,
    selectedShop,
    overrideItems,
    balancePayments,
    selectedCustomer,
    discount,
    due,
    change,
    currentShiftId,
    overrideTokenNo,
    invoiceAction,
  }) {
    const pointsEntry = paymentModes.find((m) => m.name === PAYMENT_MODES.POINTS);
    const pointsCurrency = pointsEntry?.amount || 0;
    const conversionRate = Number(selectedShop?.loyalty_point_conversion_rate || 1);
    const loyaltyPointsUsed = pointsCurrency / conversionRate;
    const finalPaymentModes = isNonChargeable ? paymentModes.map((m) => ({ ...m, amount: 0 })) : paymentModes;
    const saleBase = overrideItems ? { ...activeSale, items: overrideItems } : activeSale;
    const updatedSale = await SalesHelper.buildUpdatedSale({
      sale: { ...saleBase, customer: selectedCustomer },
      paymentModes: finalPaymentModes,
      balancePayments: isNonChargeable ? [] : balancePayments,
      selectedCustomer,
      discount: isNonChargeable ? 0 : discount,
      due: isNonChargeable ? 0 : due,
      change: isNonChargeable ? 0 : change,
      orderType: isNonChargeable ? ORDER_TYPES.NC : undefined,
      shift: currentShiftId,
      token_no: overrideTokenNo || activeSale?.token_no,
      loyaltyPointsUsed: isNonChargeable ? 0 : loyaltyPointsUsed,
      loyaltyPointsAmount: pointsCurrency,
    });
    let saleWithBill = updatedSale;
    try {
      saleWithBill = await SalesHelper.saveAndProcess(updatedSale, {
        invoiceAction,
        shop: selectedShop,
        customer: selectedCustomer,
        fallbackPrintFunction: handleWebInvoicePrint,
      });
    } catch (err) {
      console.error("Failed to assign bill number:", err);
    }
    return {
      ...saleWithBill,
      payment_modes: (saleWithBill.payment_modes || []).filter(
        (paymentMode) => paymentMode.name !== PAYMENT_MODES.POINTS,
      ),
      status: SALE_STATUS.PAID,
    };
  }

  async function prepareTargetSaleForPrint(params) {
    if (params.isEstimate) {
      return prepareEstimateForPrint(params.activeSale);
    }
    return prepareRegularSaleForPrint(params);
  }

  function validatePrintAction(action, isNonChargeable, isEstimate) {
    if (!activeSale || !activeSale?.items || activeSale?.items.length === 0) {
      toast.error("No sale/items to print.");
      return false;
    }
    if (action === INVOICE_ACTIONS.PAYMENT) {
      setShowBalanceDialog(true);
      return false;
    }
    if (!isNonChargeable && !isEstimate && due > 0) {
      toast.error("Tender is less than the bill total.");
      return false;
    }
    return true;
  }

  const getRemainingDraftInvoices = useCallback(
    async (excludeLocalId) => {
      const activeSales = await refreshSales();
      return (activeSales || []).filter(
        (inv) =>
          String(inv.local_id) !== String(excludeLocalId) &&
          (!inv.status || inv.status === SALE_STATUS.DRAFT) &&
          !inv.bill_no,
      );
    },
    [refreshSales],
  );

  async function handlePostPrintNavigation(finalSale) {
    try {
      const remainingInvoices = await getRemainingDraftInvoices(finalSale?.local_id);

      if (remainingInvoices.length > 0) {
        setActiveSale(remainingInvoices[0]);
      } else {
        await createNewSale();
      }
      resetLocalState();
    } catch (error) {
      console.error("Error updating sales after print:", error);
      toast.error("Invoice saved & printed, but refreshing sales list failed.");
      try {
        await createNewSale();
        resetLocalState();
      } catch (fallbackError) {
        console.error("Error creating new sale during post-print fallback:", fallbackError);
      }
    }
  }

  async function executePrintFlow(invoiceAction, isEstimate, isNonChargeable, overrideItems, overrideTokenNo) {
    const finalSale = await prepareTargetSaleForPrint({
      isEstimate,
      activeSale,
      isNonChargeable,
      paymentModes,
      selectedShop,
      overrideItems,
      balancePayments,
      selectedCustomer,
      discount,
      due,
      change,
      currentShiftId,
      overrideTokenNo,
      invoiceAction,
    });
    setPrintSale(finalSale);
    finalizeEstimateOrSaleSync(isEstimate, finalSale, selectedCustomer, updateCustomerAfterSale);
    await handlePostPrintNavigation(finalSale);
  }

  const createSale = async (
    overrideAction,
    { isNonChargeable = false, isEstimate = false, overrideItems = null, overrideTokenNo = null } = {},
  ) => {
    if (isLoading || saleDialogState.open) return;
    const terminalSettings = TerminalHelper.getTerminalSettings();
    const invoiceAction = overrideAction || terminalSettings.defaultInvoiceAction || INVOICE_ACTIONS.PRINT;
    if (!validatePrintAction(invoiceAction, isNonChargeable, isEstimate)) return;

    enterPrintConfirmCountRef.current = 0;
    setLoading(true);
    openSaleProcessing(invoiceAction);

    try {
      await executePrintFlow(invoiceAction, isEstimate, isNonChargeable, overrideItems, overrideTokenNo);
      openSaleSuccess(invoiceAction);
    } catch (error) {
      console.error(error?.message || "Failed to print invoice.");
      closeSaleDialog();
    } finally {
      setLoading(false);
    }
  };

  const onDirectPrintClick = () => {
    createSale(INVOICE_ACTIONS.PRINT);
  };

  const onSaveInvoiceClick = () => {
    createSale(INVOICE_ACTIONS.SAVE);
  };

  const onSaveAndWhatsAppClick = () => {
    createSale(INVOICE_ACTIONS.SAVE_AND_WHATSAPP);
  };

  const onPrintAndWhatsAppClick = () => {
    createSale(INVOICE_ACTIONS.PRINT_AND_WHATSAPP);
  };

  const onNonChargeableClick = async () => {
    if (!selectedCustomer) {
      toast.error("Please select a customer for non-chargeable sale.");
      return;
    }
    await createSale(INVOICE_ACTIONS.PRINT, { isNonChargeable: true });
  };

  const handleOpenPaymentQr = () => {
    const validation = InvoiceActionHelper.validatePaymentQr(due);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }
    setShowPaymentQrDialog(true);
  };

  const onKotAndPrintClick = async () => {
    const result = await KotManager.processKOT({
      activeSale,
      categories,
    });

    let itemsToPass = null;
    let tokenNoToPass = null;
    if (result) {
      itemsToPass = result.updatedItems;
      tokenNoToPass = result.token_no;
    }
    await createSale(undefined, { overrideItems: itemsToPass, overrideTokenNo: tokenNoToPass });
  };

  const handleEstimateClick = async () => {
    await createSale(undefined, { isEstimate: true });
  };

  const handleQrPaymentReceived = async () => {
    const finalUpiAmount = Math.max((activeSale?.payable || 0) - amountReceived, 0);
    const updatedModes = InvoiceActionHelper.getUpdatedPaymentModesWithQr(paymentModes, finalUpiAmount, UPI_MODES);

    setPaymentModes(updatedModes);
    setShowPaymentQrDialog(false);

    setTimeout(() => {
      createSale();
    }, 200);
  };

  const onPrintClickRef = useRef(createSale);
  onPrintClickRef.current = createSale;

  const onDirectPrintClickRef = useRef(onDirectPrintClick);
  onDirectPrintClickRef.current = onDirectPrintClick;

  const handleKeypadInputRef = useRef(handleKeypadInput);
  handleKeypadInputRef.current = handleKeypadInput;

  const handleNextPaymentModeRef = useRef(handleNextPaymentMode);
  handleNextPaymentModeRef.current = handleNextPaymentMode;

  const handlePreviousPaymentModeRef = useRef(handlePreviousPaymentMode);
  handlePreviousPaymentModeRef.current = handlePreviousPaymentMode;

  const onSaveInvoiceClickRef = useRef(onSaveInvoiceClick);
  onSaveInvoiceClickRef.current = onSaveInvoiceClick;

  const onSaveAndWhatsAppClickRef = useRef(onSaveAndWhatsAppClick);
  onSaveAndWhatsAppClickRef.current = onSaveAndWhatsAppClick;

  const onKotAndPrintClickRef = useRef(onKotAndPrintClick);
  onKotAndPrintClickRef.current = onKotAndPrintClick;

  function computeTargetPaymentAmount(activeMode, selectedCustomer, selectedShop, currentAmount, due) {
    let targetAmount = currentAmount + due;

    if (activeMode === PAYMENT_MODES.CREDIT && selectedCustomer?.id) {
      const activeModeObj = paymentModes.find((m) => m.name === activeMode);
      const maxAllowed = CustomerHelper.getAvailableCredit(selectedCustomer) + (activeModeObj?.initialAmount || 0);
      if (targetAmount > maxAllowed) {
        toast.error(`Amount exceeds available credit limit (${maxAllowed})`);
        targetAmount = maxAllowed;
      }
    }

    if (activeMode === PAYMENT_MODES.POINTS && selectedCustomer?.id) {
      const activeModeObj = paymentModes.find((m) => m.name === activeMode);
      const points = Number(selectedCustomer.loyalty_points || selectedCustomer.points || 0);
      const conversionRate = Number(selectedShop?.loyalty_point_conversion_rate || 1);
      const maxAllowedCurrency = points * conversionRate + (activeModeObj?.initialAmount || 0);
      if (targetAmount > maxAllowedCurrency) {
        toast.error(`Amount exceeds available points value (${maxAllowedCurrency})`);
        targetAmount = maxAllowedCurrency;
      }
    }

    return targetAmount;
  }

  const handleKeypadEnter = () => {
    if (!activeSale || !activeSale?.items || activeSale?.items.length === 0) {
      return;
    }

    if (due > 0 && !activeMode) {
      handleKeypadFocus();
      keyPadRef.current?.focus();
      return;
    }

    if (due > 0 && activeMode) {
      const activeModeObj = paymentModes.find((m) => m.name === activeMode);
      const currentAmount = activeModeObj?.amount || 0;
      const targetAmount = computeTargetPaymentAmount(activeMode, selectedCustomer, selectedShop, currentAmount, due);

      setPaymentModes((prevModes) =>
        prevModes.map((mode) => {
          if (mode.name !== activeMode) return mode;
          return {
            ...mode,
            amount: targetAmount,
          };
        }),
      );
      setKeypadString(targetAmount.toString());
      enterPrintConfirmCountRef.current = 0;
    } else if (due === 0 || amountReceived > 0) {
      if (enterPrintConfirmCountRef.current === 0) {
        setActiveMode(null);
        setTimeout(() => {
          printButtonRef.current?.focus();
        }, 100);
        enterPrintConfirmCountRef.current = 1;
      } else {
        enterPrintConfirmCountRef.current = 0;
        onPrintClickRef.current();
      }
    }
  };
  const handleKeypadEnterRef = useRef(handleKeypadEnter);
  handleKeypadEnterRef.current = handleKeypadEnter;

  function handleERPFunctionKeys(event, refs) {
    const {
      productInputRef,
      customerInputRef,
      onDirectPrintClickRef,
      onPrintClickRef,
      onSaveInvoiceClickRef,
      onKotAndPrintClickRef,
      onSaveAndWhatsAppClickRef,
    } = refs;

    const keyMap = {
      [KEYBOARD_KEYS.F1]: () => productInputRef.current?.focus(),
      [KEYBOARD_KEYS.F2]: () => {
        customerInputRef.current?.focus();
        customerInputRef.current?.select?.();
      },
      [KEYBOARD_KEYS.F4]: () => onPrintClickRef.current(),
      [KEYBOARD_KEYS.F5]: () => onSaveInvoiceClickRef.current(),
      [KEYBOARD_KEYS.F6]: () => onKotAndPrintClickRef.current(),
      [KEYBOARD_KEYS.F7]: () => onSaveAndWhatsAppClickRef.current(),
      [KEYBOARD_KEYS.F8]: () => onDirectPrintClickRef.current(),
    };

    const action = keyMap[event.key];
    if (action) {
      event.preventDefault();
      event.stopImmediatePropagation();
      action();
      return true;
    }
    return false;
  }

  function handleERPActiveModeKeys(event, handleKeypadInputRef) {
    if (event.key >= "0" && event.key <= "9") {
      event.preventDefault();
      handleKeypadInputRef.current(event.key);
    } else if (event.key === KEYBOARD_KEYS.DOT) {
      event.preventDefault();
      handleKeypadInputRef.current(KEYPAD_BUTTONS.DOT);
    } else if (event.key === KEYBOARD_KEYS.BACKSPACE) {
      event.preventDefault();
      handleKeypadInputRef.current(KEYPAD_BUTTONS.DELETE);
    } else if (event.key === KEYBOARD_KEYS.C_LOWER || event.key === KEYBOARD_KEYS.C_UPPER) {
      event.preventDefault();
      handleKeypadInputRef.current(KEYPAD_BUTTONS.CLEAR);
    } else if (event.key === KEYBOARD_KEYS.MINUS) {
      event.preventDefault();
      handleKeypadInputRef.current(KEYPAD_BUTTONS.PLUS_MINUS);
    }
  }

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (isLoading || saleDialogState.open) return;
      if (showBalanceDialog || showAddCustomer) return;
      if (isScanningBarcode() || event.defaultPrevented) return;

      const functionKeysHandled = handleERPFunctionKeys(event, {
        productInputRef,
        customerInputRef,
        onDirectPrintClickRef,
        onPrintClickRef,
        onSaveInvoiceClickRef,
        onKotAndPrintClickRef,
        onSaveAndWhatsAppClickRef,
      });
      if (functionKeysHandled) return;

      if (
        event.target &&
        (SCANNER_RESTRICTED_INPUTS.includes(event.target.tagName) || event.target.isContentEditable)
      ) {
        return;
      }

      if (event.key === KEYBOARD_KEYS.ENTER) {
        if (event.target?.tagName === HTML_TAGS.BUTTON) {
          return;
        }
        event.preventDefault();
        handleKeypadEnterRef.current();
      } else if (event.key === KEYBOARD_KEYS.ARROW_RIGHT) {
        event.preventDefault();
        handleNextPaymentModeRef.current();
      } else if (event.key === KEYBOARD_KEYS.ARROW_LEFT) {
        event.preventDefault();
        handlePreviousPaymentModeRef.current();
      } else if (activeMode) {
        handleERPActiveModeKeys(event, handleKeypadInputRef);
      }
    };

    const unsubscribeFn = eventsEmitter.on(EVENTS.KEY_DOWN, handleKeyDown);
    return unsubscribeFn;
  }, [isLoading, saleDialogState.open, showBalanceDialog, showAddCustomer, activeMode]);

  const invoiceFormat = SalesHelper.getInvoicePrinterFormat();

  const InvoiceComponent = useMemo(() => {
    if (invoiceFormat === INVOICE_FORMATS.A4) {
      return <A4Invoice ref={printComponentRef} sale={printSale} shop={selectedShop} />;
    } else if (invoiceFormat === INVOICE_FORMATS.A5) {
      return <A5Invoice ref={printComponentRef} sale={printSale} shop={selectedShop} />;
    } else {
      return <ThreeInchInvoice ref={printComponentRef} sale={printSale} shop={selectedShop} />;
    }
  }, [invoiceFormat, printSale, selectedShop]);

  const handleAddNewInvoice = async () => {
    try {
      setLoading(true);
      await createNewSale();
    } catch (err) {
      console.error("Failed to create new sale", err);
      toast.error("Failed to create new sale");
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async (invoice, description) => {
    try {
      setSaleToDelete(null);
      await handleDeleteSale(invoice?.local_id, description);
      resetLocalState();

      const remainingInvoices = await getRemainingDraftInvoices(invoice?.local_id);

      if (remainingInvoices.length === 0) {
        await createNewSale();
      } else {
        const nextSale = remainingInvoices[0];
        if (nextSale) {
          setActiveSale(nextSale);
        } else {
          await createNewSale();
        }
      }
    } catch (error) {
      console.error("Error deleting sale:", error);
      toast.error(error?.message || "Failed to delete invoice.");
    }
  };

  const amountReceivedRef = useRef(null);

  const handleUpdateRowQuantity = async (row, newQty) => {
    const qty = Number(newQty);
    if (qty < 0) return;

    if (qty === 0) {
      await removeSaleItemFromActiveSale(row.localId);
      return;
    }

    const currentQty = row.qty || 0;
    const quantity = qty - currentQty;

    if (quantity === 0) return;

    if (quantity > 0) {
      await increaseSaleItemInActiveSale(row.localId, { quantity });
    } else {
      await reduceSaleItemFromActiveSale(row.localId, { quantity: Math.abs(quantity) });
    }
  };

  const handleUpdateRowDiscount = (row, newDiscountPercentage) => {
    let discountPercentage = Number(newDiscountPercentage) || 0;
    if (discountPercentage > 100) discountPercentage = 100;
    if (discountPercentage < 0) discountPercentage = 0;
    saleItemUpdateHandler({ ...row, discountPercentage });
  };

  const handleUpdateRowTaxAmount = (row, newTaxAmount) => {
    const taxAmount = Number(newTaxAmount) || 0;
    const priceExcludingTax = row.rate * row.qty - (row.discount || 0);
    if (priceExcludingTax <= 0) return;

    const taxPercentage = NumberFormatHelper.amountExtendedRoundOff((taxAmount / priceExcludingTax) * 100);
    saleItemUpdateHandler({ ...row, taxPercentage });
  };

  const handleUpdateRowRate = (row, newRate) => {
    const rate = Number(newRate) || 0;
    const taxPreference = TaxHelper.getTaxPreference();
    const isExcludingTax = taxPreference === TAX_PREFERENCES.EXCLUDING_TAX;

    // Save manualRate so the user override persists across quantity, discount, and tax updates
    saleItemUpdateHandler({
      ...row,
      manualRate: rate,
      rate: isExcludingTax ? rate : undefined,
      rateAndTax: isExcludingTax ? undefined : rate,
    });
  };

  const handleUpdateRowProduct = async (row, newProduct) => {
    if (!newProduct) return;
    if (newProduct.id === row.productId) return;

    await removeSaleItemFromActiveSale(row.id);
    await handleAddItemToSale(newProduct, row.qty);
  };

  useEffect(() => {
    if (selectedProduct) {
      const price = getEffectivePrice(selectedProduct);
      const newRate = TaxHelper.getItemUnitRate(
        price,
        itemTaxPercentage,
        selectedProduct.cessPercentage ?? selectedProduct.cess ?? 0,
        itemDiscountPercentage,
      );
      setItemRate(NumberFormatHelper.amountExtendedRoundOff(newRate));
    }
  }, [
    selectedPriceCategory,
    selectedCustomer,
    priceCategoryItems,
    getEffectivePrice,
    itemDiscountPercentage,
    itemTaxPercentage,
    selectedProduct,
  ]);

  const handleSelectSearchProduct = useCallback(
    (product) => {
      setSelectedProduct(product);
      setProductSearchText(product?.name || "");
      if (product) {
        setItemTaxPercentage(product.taxPercentage ?? product.tax ?? 0);
        setItemDiscountPercentage(0);
        const price = getEffectivePrice(product);
        const initialRate = TaxHelper.getItemUnitRate(
          price,
          product.taxPercentage ?? product.tax ?? 0,
          product.cessPercentage ?? product.cess ?? 0,
          0,
        );
        setItemRate(NumberFormatHelper.amountExtendedRoundOff(initialRate));
      }
    },
    [
      getEffectivePrice,
      setSelectedProduct,
      setProductSearchText,
      setItemTaxPercentage,
      setItemDiscountPercentage,
      setItemRate,
    ],
  );

  const handleERPScanStart = useCallback(
    ({ firstKey } = {}) => {
      resetProductStagingState();
      if (productInputRef.current) {
        restoreInputElementValue(productInputRef.current, "");
      }

      if (Date.now() - lastKeypadInputTimeRef.current < KEYPAD_SCANNER_DEBOUNCE_MS && activeMode) {
        setKeypadString((prevStr) => {
          let restoredStr = prevStr;
          if (firstKey && restoredStr.endsWith(firstKey)) {
            restoredStr = restoredStr.slice(0, -firstKey.length);
          } else if (restoredStr.length > 0) {
            restoredStr = restoredStr.slice(0, -1);
          }
          const restoredAmount = Number(restoredStr) || 0;
          setPaymentModes((prevModes) =>
            prevModes.map((mode) => {
              if (mode.name !== activeMode) return mode;
              return {
                ...mode,
                amount: restoredAmount,
              };
            }),
          );
          return restoredStr;
        });
      }
    },
    [activeMode, setPaymentModes, resetProductStagingState],
  );

  const handleERPPreScan = useCallback(
    async (scannedCode) => {
      if (scannedCode.startsWith("$")) {
        resetProductStagingState();
      } else if (selectedProduct) {
        try {
          await handleAddSelectedProduct();
        } catch (err) {
          console.error("Failed to add staged product before barcode scan:", err);
        }
      }
    },
    [selectedProduct, handleAddSelectedProduct, resetProductStagingState],
  );

  const handleERPScanSuccess = useCallback(() => {
    resetProductStagingState();
    productInputRef.current?.blur?.();
  }, [resetProductStagingState]);

  const handleUpdateItemDiscount = (val) => {
    let discount = Number(val) || 0;
    if (discount > 100) discount = 100;
    if (discount < 0) discount = 0;
    setItemDiscountPercentage(discount);

    if (selectedProduct) {
      const price = getEffectivePrice(selectedProduct);
      const newRate = TaxHelper.getItemUnitRate(
        price,
        itemTaxPercentage,
        selectedProduct.cessPercentage ?? selectedProduct.cess ?? 0,
        discount,
      );
      setItemRate(NumberFormatHelper.amountExtendedRoundOff(newRate));
    }
  };

  const handleUpdateItemRate = (val) => {
    setItemRate(Number(val) || 0);
  };

  const balancePaymentModeLabel = useMemo(() => {
    const activeBalanceModes = (balancePayments || []).filter((p) => p.amount > 0);
    if (activeBalanceModes.length > 0) {
      return activeBalanceModes.map((p) => p.name).join(", ");
    }
    return contextPaymentModes?.[0]?.name;
  }, [balancePayments, contextPaymentModes]);

  return (
    <Grid className={styles.pageWrap}>
      <Grid className={styles.salesLayout}>
        {/* Left Section  */}
        <Grid item className={styles.contentArea}>
          <Grid className={styles.tabSelector}>
            <div className={styles.addNewBtn} onClick={handleAddNewInvoice}>
              Add new
            </div>
            <div className={styles.invoiceSection}>
              {activeInvoices.map((invoice, index) => (
                <div
                  key={invoice.local_id}
                  className={`${styles.tabItem} ${selectedInvoice === index ? styles.activeInvoice : ""}`}
                  onClick={() => {
                    setActiveSale(invoice);
                  }}
                >
                  Invoice {invoice.local_id}
                  {selectedInvoice === index && (
                    <span
                      className={styles.deleteTabIcon}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSaleToDelete(invoice);
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 16, marginLeft: "8px" }} />
                    </span>
                  )}
                </div>
              ))}
            </div>
          </Grid>
          <Grid className={`${styles.invoiceInfo} ${selectedCustomer?.id ? styles.customerSelected : ""}`}>
            {/* Invoice Details Section */}
            <Grid item xs={12} md={4}>
              <Grid className={styles.sectionBox}>
                <h3 className={styles.sectionTitle}>Invoice Details</h3>
                <Grid className={styles.inputGroup}>
                  <label className={styles.label}>Invoice no</label>
                  <WhiteTextField
                    value={activeSale?.local_id || ""}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <Tooltip
                          title="This field cannot be edited"
                          arrow
                          slotProps={{
                            tooltip: {
                              sx: {
                                backgroundColor: "rgba(249, 209, 212, 1)",
                                color: "#2b2b2b",
                                fontSize: "12px",
                                fontWeight: "600",
                                padding: "8px 12px",
                                borderRadius: "6px",
                                boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                              },
                            },
                            arrow: {
                              sx: { color: "rgba(249, 209, 212, 1)" },
                            },
                          }}
                        >
                          <InfoOutlinedIcon
                            style={{
                              fontSize: 18,
                              color: "#333",
                              cursor: "pointer",
                            }}
                          />
                        </Tooltip>
                      ),
                    }}
                    sx={{ width: "150px", "& input": { color: "#E50218 !important", fontWeight: "600" } }}
                  />
                </Grid>
                <Grid className={styles.inputGroup}>
                  <label className={styles.label}>Date</label>
                  <WhiteTextField
                    type="date"
                    value={activeSale?.time ? activeSale.time.split("T")[0] : ""}
                    onChange={(e) => handleSetDate(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <CalendarIcon style={{ width: 18, height: 18 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      width: "150px",
                      "& .MuiOutlinedInput-root": {
                        paddingRight: "12px",
                        position: "relative",
                        height: "40px",
                        background: "#fff",
                      },
                      "& input": {
                        paddingLeft: "12px !important",
                        cursor: "pointer",
                        fontSize: "14px",
                      },
                      "& input::-webkit-calendar-picker-indicator": {
                        position: "absolute",
                        left: 0,
                        top: 0,
                        width: "100%",
                        height: "100%",
                        margin: 0,
                        padding: 0,
                        cursor: "pointer",
                        opacity: 0,
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Customer Details */}
            <Grid item xs={12} md={4}>
              <Grid className={styles.sectionBox}>
                <h3 className={styles.sectionTitle}>Customer Details</h3>

                <div className={styles.customerGrid}>
                  <div style={{ marginRight: "5px" }}>
                    <Grid className={styles.inputGroup}>
                      <label className={styles.label}>Name</label>
                      {/* Searchable Select for Customer */}
                      <Autocomplete
                        sx={{ minWidth: "0", width: "100%" }}
                        options={customers || []}
                        getOptionLabel={(option) => option.name || ""}
                        filterOptions={customFilterOptions}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={selectedCustomer?.id ? selectedCustomer : null}
                        onChange={handleCustomerChange}
                        onInputChange={(event, newInputValue) => {
                          setCustomerSearchText(newInputValue);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === KEYBOARD_KEYS.ENTER) {
                            focusProductInput();
                          }
                        }}
                        noOptionsText={
                          <div>
                            <div className={styles.noOptionsText}>{customerSearchText || "customers"} not found. </div>
                            <div
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setShowAddCustomer(true);
                              }}
                              className={styles.addCustomerLink}
                            >
                              Add new customer?
                            </div>
                          </div>
                        }
                        autoHighlight
                        renderOption={(props, option, state) => (
                          <li {...props} key={`${option.id}-${state.index}`} style={{ fontSize: "13px" }}>
                            {option.name} {option.mobile ? `(${option.mobile})` : ""}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            inputRef={customerInputRef}
                            size="small"
                            fullWidth
                            sx={{
                              backgroundColor: "white",
                              borderRadius: "4px",
                              "& .MuiOutlinedInput-root input:-webkit-autofill": {
                                WebkitBoxShadow: "0 0 0 1000px #ffffff inset !important",
                                WebkitTextFillColor: "#333 !important",
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid className={styles.inputGroup}>
                      <label className={styles.label}>Phone No</label>
                      <WhiteTextField value={selectedCustomer?.mobile || ""} />
                    </Grid>
                  </div>

                  <div>
                    <Grid className={styles.inputGroup}>
                      <label className={styles.label}>GST</label>
                      <WhiteTextField value={selectedCustomer?.trn_number || ""} />
                    </Grid>
                    <Grid className={styles.inputGroup}>
                      <label className={styles.label}>Address</label>
                      <WhiteTextField value={selectedCustomer?.location || ""} />
                    </Grid>
                  </div>
                </div>
              </Grid>
            </Grid>

            {/* Order Configuration */}
            <Grid className={styles.sectionBox} style={{ borderRight: "none" }}>
              <h3 className={styles.sectionTitle}>Order Configuration</h3>
              <Grid className={styles.orderConfig}>
                <Grid className={styles.inputGroup}>
                  <label className={styles.label}>Salesperson</label>
                  <Autocomplete
                    sx={{ minWidth: "0", width: "100%" }}
                    options={salesPersons}
                    getOptionLabel={(option) => option.name}
                    filterOptions={customFilterOptions}
                    value={selectedSalesPerson}
                    onChange={handleSalesPersonChange}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    autoHighlight
                    renderOption={(props, option, state) => (
                      <li {...props} key={`${option.id}-${state.index}`} style={{ fontSize: "13px" }}>
                        {option.name}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        sx={{
                          backgroundColor: "white",
                          "& .MuiOutlinedInput-root": {
                            height: "40px",
                            paddingRight: "8px",
                          },
                          "& .MuiOutlinedInput-input": {
                            padding: "8px 14px",
                          },
                          "& .MuiOutlinedInput-root input:-webkit-autofill": {
                            WebkitBoxShadow: "0 0 0 1000px #ffffff inset !important",
                            WebkitTextFillColor: "#333 !important",
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid className={styles.inputGroup}>
                  <label className={styles.label}>Price categ.</label>
                  <Autocomplete
                    sx={{ minWidth: "0", width: "100%" }}
                    options={[{ id: null, name: "Default" }, ...(priceCategories || [])]}
                    getOptionLabel={(option) => option.name || "Default"}
                    filterOptions={customFilterOptions}
                    value={selectedPriceCategory || { id: null, name: "Default" }}
                    onChange={(event, newValue) => handleChangePriceCategory(newValue?.id ? newValue : null)}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    autoHighlight
                    renderOption={(props, option, state) => (
                      <li {...props} key={`${option.id}-${state.index}`} style={{ fontSize: "13px" }}>
                        {option.name}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        sx={{
                          backgroundColor: "white",
                          "& .MuiOutlinedInput-root": {
                            height: "40px",
                            paddingRight: "8px",
                          },
                          "& .MuiOutlinedInput-input": {
                            padding: "8px 14px",
                          },
                          "& .MuiOutlinedInput-root input:-webkit-autofill": {
                            WebkitBoxShadow: "0 0 0 1000px #ffffff inset !important",
                            WebkitTextFillColor: "#333 !important",
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          {selectedCustomer && selectedCustomer.id && (
            <Grid item className={styles.customerMetricsOverlay}>
              <div className={styles.metricItem}>
                <span className={styles.metricLabel}>Credit Limit:</span>
                <span className={styles.metricValue}>
                  <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
                  {NumberFormatHelper.customerAmountFormat(selectedCustomer.credit_limit)}
                </span>
              </div>

              <div className={styles.metricItem}>
                <span className={styles.metricLabel}>Balance:</span>
                <span className={`${styles.metricValue} ${selectedCustomer.balance < 0 ? styles.negative : ""}`}>
                  <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
                  {NumberFormatHelper.customerAmountFormat(selectedCustomer.balance)}
                </span>
              </div>

              <div className={styles.metricItem}>
                <span className={styles.metricLabel}>Loyalty Card:</span>
                <span className={styles.metricValue}>{selectedCustomer.loyalty_card_no || "N/A"}</span>
              </div>

              <div className={styles.metricItem}>
                <span className={styles.metricLabel}>Loyalty Points:</span>
                <span className={styles.metricValue}>
                  <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
                  {selectedCustomer.points || "0"}
                </span>
              </div>
            </Grid>
          )}

          {/* 2. PRODUCT TABLE SECTION */}
          <ERPTable
            saleItems={saleItems}
            items={availableItems}
            handleUpdateRowProduct={handleUpdateRowProduct}
            handleUpdateRowQuantity={handleUpdateRowQuantity}
            handleUpdateRowDiscount={handleUpdateRowDiscount}
            handleUpdateRowTaxAmount={handleUpdateRowTaxAmount}
            handleUpdateRowRate={handleUpdateRowRate}
            handleRemoveItemFromSale={removeSaleItemFromActiveSale}
            selectedProduct={selectedProduct}
            setSelectedProduct={handleSelectSearchProduct}
            productSearchText={productSearchText}
            setProductSearchText={setProductSearchText}
            quantity={itemQuantity}
            setQuantity={setItemQuantity}
            itemDiscountPercentage={itemDiscountPercentage}
            handleUpdateItemDiscount={handleUpdateItemDiscount}
            itemTaxPercentage={itemTaxPercentage}
            itemRate={itemRate}
            handleUpdateItemRate={handleUpdateItemRate}
            handleAddSelectedProduct={handleAddSelectedProduct}
            productInputRef={productInputRef}
            amountReceivedRef={amountReceivedRef}
            keyPadRef={keyPadRef}
            onKeypadFocus={handleKeypadFocus}
          />
        </Grid>

        {/* Right Section (Cart/Payment area) */}
        <Grid item className={styles.cart}>
          {/* Summary Section */}
          <div className={styles.summarySection}>
            <div className={styles.summaryRow}>
              <h3>Total</h3>
              <span className={styles.summaryValue}>
                <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
                {NumberFormatHelper.amountRoundOff(activeSale?.total || 0)}
              </span>
            </div>
            <div className={styles.summaryRow}>
              <h3>Taxes</h3>
              <span className={styles.summaryValue}>
                <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
                {NumberFormatHelper.amountRoundOff((activeSale?.tax || 0) + (activeSale?.cess || 0))}
              </span>
            </div>
            <div className={styles.summaryRow}>
              <h3>Discount</h3>
              <span className={styles.summaryValue}>
                <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>{" "}
                {NumberFormatHelper.amountRoundOff(
                  (Number(activeSale?.discount_applied_on_items) || 0) + (Number(activeSale?.discount) || 0),
                )}
              </span>
            </div>
            <div className={styles.summaryRow}>
              <h3>Round Off</h3>
              <span className={styles.summaryValue}>
                <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
                {NumberFormatHelper.roundOff(activeSale?.roundOff || 0)}
              </span>
            </div>
            <hr className={styles.divider} />
            <div className={`${styles.summaryRow} ${styles.netTotal}`}>
              <h3>Net Total</h3>
              <span className={styles.totalValue}>
                <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
                {NumberFormatHelper.amountRoundOff(activeSale?.payable || 0)}
              </span>
            </div>
            <hr className={styles.divider} />
          </div>

          {/* Amount Received Section */}
          <div className={styles.balanceCard}>
            <div className={styles.summarySection}>
              <div className={styles.summaryRow}>
                <h3>{prepaidAmount > 0 ? "Prepaid Amount" : "Amount Received"}</h3>
                <span className={styles.summaryValue}>
                  <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
                  {prepaidAmount > 0 ? NumberFormatHelper.roundOff(prepaidAmount) : amountReceived}
                </span>
              </div>
              <div className={styles.summaryRow}>
                <h3>Due Amount</h3>
                <span className={styles.summaryValue}>
                  <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
                  {NumberFormatHelper.roundOff(due)}
                </span>
              </div>
            </div>
            <div className={styles.balanceText}>
              Pay Change:
              <span className={styles.balanceValue}>
                <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
                {NumberFormatHelper.amountExtendedRoundOff(change) || 0}
              </span>
              Via
              <span style={{ color: "#e50218" }}>{balancePaymentModeLabel}</span>
              <button className={styles.changeBtn} onClick={() => setShowBalanceDialog(true)} disabled={change <= 0}>
                Change
              </button>
            </div>
          </div>

          {/* Payment Modes Grid */}
          <div ref={paymentTabsRef} className={styles.paymentTabs}>
            {sortedPaymentModes.map((mode, index) => (
              <button
                key={mode.id}
                onClick={() => {
                  if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                  }
                  setActiveMode(mode.name);
                  setKeypadString(mode.amount !== 0 ? mode.amount.toString() : "");
                }}
                onMouseDown={(e) => e.preventDefault()}
                tabIndex={-1}
                className={`${index === 0 ? styles.firstBtn : ""} ${
                  index === sortedPaymentModes.length - 1 ? styles.lastBtn : ""
                } ${mode.name === activeMode ? styles.activeTab : ""}`}
              >
                <div className={styles.paymentHead}>
                  <h3 className={styles.paymentLabel}>{mode.name}</h3>
                </div>
                <h2 className={styles.amount}>
                  <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>{" "}
                  {NumberFormatHelper.roundOff(mode.amount)}
                </h2>
              </button>
            ))}
          </div>

          {/* KeyPadVertical Section */}
          <KeyPadVertical ref={keyPadRef} onInput={handleKeypadInput} onEnter={handleKeypadEnter} />

          {/* Action Button */}
          <CheckoutActionMenu
            ref={printButtonRef}
            className={styles.actionBtnContainer}
            onDefaultActionClick={createSale}
            onPrintClick={onDirectPrintClick}
            onSaveInvoice={onSaveInvoiceClick}
            onSaveAndWhatsApp={onSaveAndWhatsAppClick}
            onPrintAndWhatsApp={onPrintAndWhatsAppClick}
            onNonChargeable={onNonChargeableClick}
            onPaymentQr={handleOpenPaymentQr}
            onKotAndPrint={onKotAndPrintClick}
            onEstimate={handleEstimateClick}
            isLoading={isLoading}
            buttonLabel={InvoiceActionHelper.getInvoiceActionButtonLabel()}
          />
        </Grid>
      </Grid>
      <div
        style={{
          position: "absolute",
          left: "-10000px",
          top: "-10000px",
          visibility: "hidden",
        }}
      >
        {InvoiceComponent}
      </div>

      {showBalanceDialog && (
        <BalanceDialog
          balance={change}
          paymentModes={sortedPaymentModes.filter((m) => m.name !== "Discount")}
          onSave={(data) => {
            setBalancePayments(data);
            setShowBalanceDialog(false);
          }}
          onClose={() => setShowBalanceDialog(false)}
        />
      )}

      {showPaymentQrDialog && (
        <GeneratePaymentQrDialog
          open={showPaymentQrDialog}
          onClose={() => setShowPaymentQrDialog(false)}
          onSaveAndClose={handleQrPaymentReceived}
          grossPayable={activeSale?.payable || 0}
          due={due}
          selectedCustomer={selectedCustomer}
        />
      )}

      <CartDeleteConfirmPopup
        open={Boolean(saleToDelete)}
        onClose={() => setSaleToDelete(null)}
        onConfirm={(description) => confirmDelete(saleToDelete, description)}
      />

      {showAddCustomer && (
        <AddCustomer
          searchText={customerSearchText}
          closeDialog={() => setShowAddCustomer(false)}
          addCustomerHandler={async (data) => {
            try {
              const newCustomer = await addCustomer(data);
              setShowAddCustomer(false);
              if (newCustomer) {
                handleCustomerChange(null, newCustomer);
              }
            } catch (error) {
              toast.error(error || "Failed to create customer");
            }
          }}
        />
      )}

      <BarcodeScanner
        isDisabled={
          Boolean(saleToDelete) ||
          isLoading ||
          showBalanceDialog ||
          showAddCustomer ||
          showPaymentQrDialog ||
          saleDialogState.open
        }
        onScanStart={handleERPScanStart}
        onPreScan={handleERPPreScan}
        onScanSuccess={handleERPScanSuccess}
      />

      <SaleStatusDialog
        open={saleDialogState.open}
        status={saleDialogState.status}
        action={saleDialogState.action}
        onClose={closeSaleDialog}
      />
    </Grid>
  );
}

export default ERPDashboard;
